import { createFileRoute } from '@tanstack/react-router';
import { AnimatedTotalRaised } from '@/routes/-components/animated-total-raised';
import { AnimateNumber } from '@worldcoo/ui/custom/animate-number';
import { useTranslation } from '@/lib/i18n';
import { Suspense, useState, useEffect, useRef } from 'react';
import { useQuery, useSuspenseQuery, queryOptions } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'motion/react';
import type { Locale } from '@worldcoo/monolith/core';

export const Route = createFileRoute('/www/.internal/counter')({
	component: Component,
});

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

/** Simulated historical total in cents */
const BASE_TOTAL_CENTS = 1_523_456_789;

/** Mulberry32 — same seed → same sequence across browsers */
function mulberry32(seed: number) {
	let s = seed | 0;
	return () => {
		s = (s + 0x6d2b79f5) | 0;
		let t = Math.imul(s ^ (s >>> 15), 1 | s);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

function pickRandom<T>(arr: T[], rng: () => number): T {
	return arr[Math.floor(rng() * arr.length)]!;
}

// ===========================================================================
//
//  SECTION 1 — Live Playthrough (seamless-loop feed)
//
// ===========================================================================

type Cause = { campaign: string; ngo: string };

type FeedDonation = {
	amountCents: number;
	offset: number;
	city: string;
	country: string;
	merchant: string;
	campaign: string;
	ngo: string;
	id: string;
};

/** Duration of one donation cycle. Donations loop seamlessly across cycles. */
const CYCLE_MS = 12_000;

/** Relative ms within each cycle where a donation appears (clustered for a natural rhythm). */
const DONATION_OFFSETS = [800, 1_300, 2_000, 3_400, 4_000, 5_400, 6_200, 6_800, 8_400, 9_800, 10_600, 11_200];

// ---------------------------------------------------------------------------
// Data pools
// ---------------------------------------------------------------------------

const CAUSES: Cause[] = [
	{ campaign: 'School meals in Senegal', ngo: 'Action Against Hunger' },
	{ campaign: 'Clean water for a village in India', ngo: 'WaterAid' },
	{ campaign: 'Winter blankets for Syria', ngo: 'UNHCR' },
	{ campaign: 'Books for kids in Colombia', ngo: 'Save the Children' },
	{ campaign: 'Plant trees in the Amazon', ngo: 'WWF' },
	{
		campaign: 'Medicine for a clinic in Kenya',
		ngo: 'Doctors Without Borders',
	},
	{ campaign: 'Shelter after the earthquake', ngo: 'Red Cross' },
	{ campaign: 'Solar lights for Mozambique', ngo: 'Greenpeace' },
	{ campaign: 'Mosquito nets in Uganda', ngo: 'UNICEF' },
	{ campaign: 'Food boxes for families', ngo: 'Oxfam' },
	{ campaign: 'Eye care in Bangladesh', ngo: 'Orbis' },
	{ campaign: 'Baby kits for new mums', ngo: 'Plan International' },
	{ campaign: 'Seeds for small farmers', ngo: 'Heifer International' },
	{ campaign: 'Build a classroom in Nepal', ngo: 'Habitat for Humanity' },
	{ campaign: 'Vaccines for newborns', ngo: 'UNICEF' },
	{ campaign: 'Ocean plastic cleanup', ngo: 'Ocean Conservancy' },
	{ campaign: 'Music lessons in favelas', ngo: 'SOS Children' },
	{ campaign: 'Water filters in Cambodia', ngo: 'WaterAid' },
	{ campaign: 'Girls back to school', ngo: 'Malala Fund' },
	{ campaign: 'Wheelchairs for kids', ngo: 'Red Cross' },
];

const CITIES = [
	{ city: 'Barcelona', country: 'ES' },
	{ city: 'Madrid', country: 'ES' },
	{ city: 'London', country: 'GB' },
	{ city: 'Paris', country: 'FR' },
	{ city: 'Berlin', country: 'DE' },
	{ city: 'Amsterdam', country: 'NL' },
	{ city: 'Lisbon', country: 'PT' },
	{ city: 'Rome', country: 'IT' },
	{ city: 'Milan', country: 'IT' },
	{ city: 'Stockholm', country: 'SE' },
	{ city: 'Dublin', country: 'IE' },
	{ city: 'Brussels', country: 'BE' },
	{ city: 'Vienna', country: 'AT' },
	{ city: 'Zurich', country: 'CH' },
	{ city: 'Copenhagen', country: 'DK' },
	{ city: 'Helsinki', country: 'FI' },
	{ city: 'Oslo', country: 'NO' },
	{ city: 'Warsaw', country: 'PL' },
	{ city: 'Prague', country: 'CZ' },
	{ city: 'Valencia', country: 'ES' },
];

const MERCHANTS = [
	'Mango',
	'Freshly Cosmetics',
	'PC Componentes',
	'Casa del Libro',
	'Laagam',
	'Minimalism Brand',
	'Blue Banana',
	'Mr. Wonderful',
	'Pompeii Brand',
	'Hawkers',
	'Scalpers',
	'Tropicfeel',
	'Brava Fabrics',
	'Hemper',
	'Coco & Eve',
	'Holaluz',
	'Wallapop',
	'Glovo',
	'Too Good To Go',
	'Back Market',
];

const COUNTRY_FLAGS: Record<string, string> = {
	ES: '🇪🇸',
	GB: '🇬🇧',
	FR: '🇫🇷',
	DE: '🇩🇪',
	NL: '🇳🇱',
	PT: '🇵🇹',
	IT: '🇮🇹',
	SE: '🇸🇪',
	IE: '🇮🇪',
	BE: '🇧🇪',
	AT: '🇦🇹',
	CH: '🇨🇭',
	DK: '🇩🇰',
	FI: '🇫🇮',
	NO: '🇳🇴',
	PL: '🇵🇱',
	CZ: '🇨🇿',
};

// ---------------------------------------------------------------------------
// Feed RNG + donation builder
// ---------------------------------------------------------------------------

function feedRandomAmount(rng: () => number): number {
	const r = rng();
	if (r < 0.6) return Math.round(rng() * 450) + 50; // 0.50 – 5.00
	if (r < 0.85) return Math.round(rng() * 1000) + 500; // 5.00 – 15.00
	return Math.round(rng() * 3500) + 1500; // 15.00 – 50.00
}

function buildCycleDonations(cycleIndex: number): FeedDonation[] {
	const rng = mulberry32(cycleIndex * 7919 + 42);
	return DONATION_OFFSETS.map((offset, i) => {
		const loc = pickRandom(CITIES, rng);
		const cause = pickRandom(CAUSES, rng);
		return {
			amountCents: feedRandomAmount(rng),
			offset,
			city: loc.city,
			country: loc.country,
			merchant: pickRandom(MERCHANTS, rng),
			campaign: cause.campaign,
			ngo: cause.ngo,
			id: `${cycleIndex}-${i}`,
		};
	});
}

function cycleTotalCents(cycleIndex: number): number {
	return buildCycleDonations(cycleIndex).reduce((sum, d) => sum + d.amountCents, 0);
}

// ---------------------------------------------------------------------------
// Animated counter with donation feed (seamless loop)
// ---------------------------------------------------------------------------

function AnimatedTotalWithFeed({ language }: { language: Locale }) {
	const startTimeRef = useRef(Date.now());
	const arrivedIdsRef = useRef(new Set<string>());
	const accumulatedCentsRef = useRef(0);
	const lastFullCycleRef = useRef(-1);

	const [counterValue, setCounterValue] = useState(BASE_TOTAL_CENTS / 100);
	const [feedItems, setFeedItems] = useState<FeedDonation[]>([]);

	useEffect(() => {
		const tick = () => {
			const elapsed = Date.now() - startTimeRef.current;
			const cycleIndex = Math.floor(elapsed / CYCLE_MS);
			const posInCycle = elapsed % CYCLE_MS;

			// Incrementally accumulate totals for completed cycles
			while (lastFullCycleRef.current < cycleIndex - 1) {
				lastFullCycleRef.current++;
				accumulatedCentsRef.current += cycleTotalCents(lastFullCycleRef.current);
			}

			const currentDonations = buildCycleDonations(cycleIndex);
			const arrived = currentDonations.filter((d) => d.offset <= posInCycle);

			// Append new arrivals to the rolling feed
			const newArrivals: FeedDonation[] = [];
			for (const d of arrived) {
				if (!arrivedIdsRef.current.has(d.id)) {
					arrivedIdsRef.current.add(d.id);
					newArrivals.push(d);
				}
			}
			if (newArrivals.length > 0) {
				setFeedItems((prev) => [...prev, ...newArrivals].slice(-20));
			}

			const arrivedCents = arrived.reduce((sum, d) => sum + d.amountCents, 0);
			setCounterValue((BASE_TOTAL_CENTS + accumulatedCentsRef.current + arrivedCents) / 100);
		};

		tick();
		const timer = setInterval(tick, 150);
		return () => clearInterval(timer);
	}, []);

	return (
		<>
			<div className="text-primary text-center text-5xl font-bold tracking-tight md:text-7xl">
				<AnimateNumber locale={language} value={counterValue} suffix="€" decimalDigits={2} />
			</div>
			<DonationFeed donations={feedItems} />
		</>
	);
}

// ---------------------------------------------------------------------------
// Donation feed — motion layout animations
// ---------------------------------------------------------------------------

const MAX_VISIBLE = 6;

const demoSharedSpring = {
	type: 'spring' as const,
	stiffness: 320,
	damping: 30,
};

const demoEnterTransition = {
	layout: demoSharedSpring,
	y: demoSharedSpring,
	opacity: { delay: 0.1, duration: 0.3, ease: 'easeOut' as const },
};

function DonationFeed({ donations }: { donations: FeedDonation[] }) {
	// oxlint-disable-next-line unicorn/no-array-reverse
	const allItems = [...donations].reverse();

	return (
		<div className="mt-4 w-full">
			{/* Waiting state */}
			<AnimatePresence>
				{allItems.length === 0 && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.3 }}
						className="flex items-center justify-center pt-4 text-xs text-gray-300"
					>
						<span className="flex items-center gap-2">
							<span className="relative flex h-1.5 w-1.5">
								<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gray-300 opacity-75" />
								<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-gray-300" />
							</span>
							Listening for donations...
						</span>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Donation rows */}
			<div className="flex flex-col items-center">
				<AnimatePresence initial={false}>
					{allItems.map((d, i) => (
						<DemoDonationRow key={d.id} donation={d} index={i} />
					))}
				</AnimatePresence>
			</div>
		</div>
	);
}

function DemoDonationRow({ donation, index }: { donation: FeedDonation; index: number }) {
	const flag = COUNTRY_FLAGS[donation.country] ?? '';
	const amount = (donation.amountCents / 100).toFixed(2);
	const targetOpacity = index >= MAX_VISIBLE ? 0 : index === 0 ? 1 : Math.max(0.25, 1 - index * 0.1);

	return (
		<motion.div
			layout
			initial={{ opacity: 0, y: -30 }}
			animate={{ opacity: targetOpacity, y: 0 }}
			transition={demoEnterTransition}
			className="w-full"
		>
			<div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 px-1 py-1.5">
				<span className="text-primary shrink-0 text-sm font-semibold tabular-nums">+{amount}€</span>
				<span className="shrink-0 text-sm text-gray-400">for</span>
				<span className="text-sm font-medium text-gray-700">{donation.campaign}</span>
				<span className="text-sm text-gray-400">in {donation.merchant},</span>
				<span className="text-sm text-gray-400">
					{flag} {donation.city}
				</span>
			</div>
		</motion.div>
	);
}

// ===========================================================================
//
//  SECTION 2 — Debug Internals (window-based counter with JSON display)
//
// ===========================================================================

type WindowDonation = { amountCents: number; timestamp: number };
type WindowResponse = {
	totalAtWindowStartCents: number;
	donations: WindowDonation[];
};

/**
 * In production the key changes every 5 minutes (10:00, 10:05…) but the
 * server returns 6 minutes of data → 1 min margin while the next batch loads.
 * Here we use 8s keys with ~9.6s of data to simulate the same overlap.
 */
const DEMO_WINDOW_MS = 8_000;

/** Random donation amount between 100 and 10000 cents (€1 – €100) */
function windowRandomAmount(rng: () => number): number {
	return Math.round(rng() * 9900) + 100;
}

function buildWindowDonations(windowStart: number, rng: () => number): WindowDonation[] {
	return [
		{ amountCents: windowRandomAmount(rng), timestamp: windowStart + 800 },
		{ amountCents: windowRandomAmount(rng), timestamp: windowStart + 1_100 },
		{ amountCents: windowRandomAmount(rng), timestamp: windowStart + 1_400 },
		{ amountCents: windowRandomAmount(rng), timestamp: windowStart + 2_900 },
		{ amountCents: windowRandomAmount(rng), timestamp: windowStart + 3_100 },
		{ amountCents: windowRandomAmount(rng), timestamp: windowStart + 3_300 },
		{ amountCents: windowRandomAmount(rng), timestamp: windowStart + 3_500 },
		{ amountCents: windowRandomAmount(rng), timestamp: windowStart + 5_500 },
		{ amountCents: windowRandomAmount(rng), timestamp: windowStart + 5_800 },
		{ amountCents: windowRandomAmount(rng), timestamp: windowStart + 6_100 },
		{ amountCents: windowRandomAmount(rng), timestamp: windowStart + 8_500 },
		{ amountCents: windowRandomAmount(rng), timestamp: windowStart + 9_200 },
	];
}

function fakeGetRecentDonations(windowStartMs: number): WindowResponse {
	const hourStart = Math.floor(windowStartMs / (60 * 60_000)) * (60 * 60_000);
	const windowIndex = Math.round((windowStartMs - hourStart) / DEMO_WINDOW_MS);

	let totalAtWindowStartCents = BASE_TOTAL_CENTS;
	for (let i = 0; i < windowIndex; i++) {
		const ws = hourStart + i * DEMO_WINDOW_MS;
		const rng = mulberry32(ws);
		const batch = buildWindowDonations(ws, rng);
		const windowEnd = ws + DEMO_WINDOW_MS;
		totalAtWindowStartCents += batch.filter((d) => d.timestamp < windowEnd).reduce((sum, d) => sum + d.amountCents, 0);
	}

	const rng = mulberry32(windowStartMs);
	const donations = buildWindowDonations(windowStartMs, rng);

	return { totalAtWindowStartCents, donations };
}

function currentDemoWindowKey() {
	return Math.floor(Date.now() / DEMO_WINDOW_MS) * DEMO_WINDOW_MS;
}

const demoRecentDonationsOptions = (windowStartMs: number) =>
	queryOptions({
		queryKey: ['demo-recent-donations', windowStartMs],
		queryFn: () => Promise.resolve(fakeGetRecentDonations(windowStartMs)),
		staleTime: 0,
	});

// ---------------------------------------------------------------------------
// DonationProgressDots
// ---------------------------------------------------------------------------

function DonationProgressDots({ donations }: { donations: WindowDonation[] }) {
	const [now, setNow] = useState(Date.now());

	useEffect(() => {
		const allDone = donations.every((d) => d.timestamp <= now);
		if (allDone) return;
		const timer = setInterval(() => setNow(Date.now()), 200);
		return () => clearInterval(timer);
	}, [donations, now]);

	const arrived = donations.filter((d) => d.timestamp <= now).length;
	const allDone = arrived === donations.length;

	return (
		<>
			<div className="flex items-center gap-2">
				{donations.map((d, i) => (
					<div
						// oxlint-disable-next-line react/no-array-index-key
						key={`${i}-${d.timestamp}`}
						className={`h-2 w-2 rounded-full transition-colors ${d.timestamp <= now ? 'bg-primary' : 'bg-gray-300'}`}
					/>
				))}
			</div>
			<p className="text-sm text-gray-500">
				{arrived === 0
					? 'Waiting for donations...'
					: allDone
						? `All ${donations.length} donations received`
						: `${arrived}/${donations.length} donations received`}
			</p>
		</>
	);
}

// ---------------------------------------------------------------------------
// JSON Data Display
// ---------------------------------------------------------------------------

function DonationJsonDisplay({
	label,
	donations,
	totalAtWindowStartCents,
	active,
}: {
	label: string;
	donations: WindowDonation[];
	totalAtWindowStartCents: number;
	active?: boolean;
}) {
	const windowDonationsCents = donations.reduce((sum, d) => sum + d.amountCents, 0);

	return (
		<div className={`rounded-lg p-4 ${active ? 'bg-primary/5 ring-primary ring-2' : 'bg-gray-50'}`}>
			<h4 className="mb-2 text-sm font-semibold text-gray-700">{label}</h4>
			<pre className="max-h-64 overflow-auto rounded bg-gray-900 p-4 text-xs text-green-400">
				{JSON.stringify(
					{
						totalAtWindowStartCents,
						totalAtWindowStartEuros: (totalAtWindowStartCents / 100).toFixed(2),
						donations: donations.map((d) => ({
							amountCents: d.amountCents,
							amountEuros: (d.amountCents / 100).toFixed(2),
							timestamp: new Date(d.timestamp).toISOString(),
						})),
						windowDonationsEuros: (windowDonationsCents / 100).toFixed(2),
						finalTotal: ((totalAtWindowStartCents + windowDonationsCents) / 100).toFixed(2),
					},
					null,
					2,
				)}
			</pre>
		</div>
	);
}

// ===========================================================================
//
//  Main page
//
// ===========================================================================

function Component() {
	const { language } = useTranslation();

	// Window-based state for the debug section
	const [windowStartMs, setWindowStartMs] = useState(currentDemoWindowKey);
	useEffect(() => {
		const timer = setInterval(() => {
			const key = currentDemoWindowKey();
			if (key !== windowStartMs) setWindowStartMs(key);
		}, 200);
		return () => clearInterval(timer);
	}, [windowStartMs]);

	const hourStart = Math.floor(windowStartMs / (60 * 60_000)) * (60 * 60_000);
	const windowIndex = Math.round((windowStartMs - hourStart) / DEMO_WINDOW_MS);

	const staticFallback = `${(BASE_TOTAL_CENTS / 100).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €`;

	return (
		<div className="flex flex-col items-center gap-2">
			{/* ---- Live Playthrough ---- */}
			<section className="mx-auto flex w-full max-w-xl flex-col items-center px-6 py-20">
				<h2 className="mb-1 text-2xl font-bold">Live Playthrough</h2>
				<p className="mb-6 text-sm text-gray-500">Simulated real-time donation feed with seamless looping</p>
				<AnimatedTotalWithFeed language={language} />
			</section>

			<hr className="mx-auto max-w-5xl border-gray-200" />

			{/* ---- Debug / Internals ---- */}
			<section className="mx-auto flex max-w-5xl flex-col gap-12 p-8 py-16">
				<div>
					<h1 className="text-4xl font-bold">Debug Internals</h1>
					<p className="mt-2 text-gray-600">
						Mirrors the production implementation using TanStack Query. The key changes every {DEMO_WINDOW_MS / 1000}s
						(simulating 5 min), and the fake server returns ~{((DEMO_WINDOW_MS * 6) / 5 / 1000).toFixed(1)}s of data
						(simulating 6 min) for a 1-min overlap margin.
					</p>
					<p className="mt-1 text-xs text-gray-400">
						Window key: {new Date(windowStartMs).toISOString()} | Index in hour: {windowIndex}
					</p>
				</div>

				<div>
					<h2 className="mb-4 text-2xl font-semibold">Window {windowIndex + 1}</h2>

					<div className="rounded-lg border p-6">
						<h3 className="text-primary text-center text-4xl font-bold">
							<Suspense fallback={<>{staticFallback}</>}>
								<SuspensedTotalRaised windowStartMs={windowStartMs} />
							</Suspense>
						</h3>
						<div className="mt-4 flex flex-col items-center gap-2">
							<Suspense>
								<SuspensedDonationDots windowStartMs={windowStartMs} />
							</Suspense>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
					<Suspense>
						<SuspensedJsonDisplays windowStartMs={windowStartMs} windowIndex={windowIndex} />
					</Suspense>
				</div>
			</section>
		</div>
	);
}

function SuspensedTotalRaised({ windowStartMs }: { windowStartMs: number }) {
	const { language } = useTranslation();
	const { data } = useSuspenseQuery(demoRecentDonationsOptions(windowStartMs));

	return (
		<AnimatedTotalRaised
			totalAtWindowStartCents={data.totalAtWindowStartCents}
			donations={data.donations}
			language={language}
		/>
	);
}

function SuspensedDonationDots({ windowStartMs }: { windowStartMs: number }) {
	const { data } = useSuspenseQuery(demoRecentDonationsOptions(windowStartMs));
	return <DonationProgressDots donations={data.donations} />;
}

function SuspensedJsonDisplays({ windowStartMs, windowIndex }: { windowStartMs: number; windowIndex: number }) {
	const { data: currentData } = useSuspenseQuery(demoRecentDonationsOptions(windowStartMs));

	const nextWindowStartMs = windowStartMs + DEMO_WINDOW_MS;
	const { data: nextData } = useQuery(demoRecentDonationsOptions(nextWindowStartMs));

	const nextTotalAtWindowStartCents = nextData?.totalAtWindowStartCents ?? currentData.totalAtWindowStartCents;
	const nextDonations = nextData?.donations ?? [];

	return (
		<>
			<DonationJsonDisplay
				label={`Current (window ${windowIndex + 1})`}
				donations={currentData.donations}
				totalAtWindowStartCents={currentData.totalAtWindowStartCents}
				active
			/>
			<DonationJsonDisplay
				label={`Next (window ${windowIndex + 2})`}
				donations={nextDonations}
				totalAtWindowStartCents={nextTotalAtWindowStartCents}
			/>
		</>
	);
}
